import { Autocomplete, TextField } from '@mui/material';
import { FC, useState } from 'react';
import MultiStageSearchMonthOption from './MultiStageSearchMonthOption';
import MultiStageSearchOption from './MultiStageSearchOption';
import {
  IconX,
  IconSearch,
  IconMapPin,
  IconArrowLeft,
  IconArrowRight,
  IconCalendarEvent,
  IconMinus,
  IconPlus,
} from '@tabler/icons-react';
import { groupDataIntoRows } from '../pages/Home/helpers/groupDataIntoRows';
import { MonthData } from '../common/constants/monthData';
import { HomeAction } from '../reducers/homeReducer';
import {
  getSearchIcon,
  getSearchType,
} from '../pages/Home/helpers/getAutoComplete';
import { useNavigate } from 'react-router-dom';
import { buildTokenizedQueryString } from '../common/utilities/encodeURIComponent';
import useGetCountries from '../hooks/useGetCountries';
import { SearchType } from '../common/constants/searchType';
import useGetRoutes from '../hooks/useGetRoutes';
import useGetBoats from '../hooks/useGetBoats';

interface Props {
  showSearchOverlay: boolean;
  setShowSearchOverlay: (showSearchOverlay: boolean) => void;
  dispatch: React.Dispatch<HomeAction>;
  destination: string;
  month: string;
  diver: number;
  currentYear: number;
  searchType: number;
  destinationId: number;
  monthId: number;
}

interface SearchData {
  label: string;
  type: number;
  id: number;
}

const SearchOverlay: FC<Props> = ({
  showSearchOverlay,
  setShowSearchOverlay,
  dispatch,
  destination,
  month,
  diver,
  currentYear,
  searchType,
  destinationId,
  monthId,
}) => {
  const navigate = useNavigate();
  const [expandWhere, setExpandWhere] = useState(false);
  const [expandWhen, setExpandWhen] = useState(false);
  const { countries } = useGetCountries();
  const { routes } = useGetRoutes();
  const { boats } = useGetBoats();

  const setDestination = (destinationName: string) => {
    dispatch({ type: 'SET_DESTINATION', payload: destinationName });
  };

  const setDestinationId = (destinationId: number) => {
    dispatch({ type: 'SET_DESTINATION_ID', payload: destinationId });
  };

  const setMonth = (month: string) => {
    dispatch({ type: 'SET_MONTH', payload: month });
  };

  const setMonthId = (monthId: number) => {
    dispatch({ type: 'SET_MONTH_ID', payload: monthId });
  };

  const setDiver = (diver: number) => {
    dispatch({ type: 'SET_DIVER', payload: diver });
  };

  const setCurrentYear = (year: number) => {
    dispatch({ type: 'SET_CURRENT_YEAR', payload: year });
  };

  const setSearchType = (searchType: number) => {
    dispatch({ type: 'SET_SEARCH_TYPE', payload: searchType });
  };

  const handleDestinationBoxClick = (
    destinationName: string,
    searchType: number,
    destinationId: number,
  ) => {
    setDestination(destinationName);
    setDestinationId(destinationId);
    setExpandWhen(true);
    setExpandWhere(false);
    setSearchType(searchType);
  };

  const handleDestinationSearchChange = (
    _event: unknown,
    newValue: string | null,
    searchType: number,
    destinationId: number,
  ) => {
    setDestination(newValue || '');
    setDestinationId(destinationId);
    setSearchType(searchType);
    setExpandWhen(true);
    setExpandWhere(false);
  };

  const handleMonthBoxClick = (monthName: string, monthId: number) => {
    setMonth(monthName);
    setMonthId(monthId);
    setExpandWhen(false);
  };

  const handleClearAll = () => {
    setDestination('Anywhere');
    setDestinationId(0);
    setMonth('Anytime');
    setMonthId(0);
    setDiver(1);
    setCurrentYear(new Date().getFullYear());
  };

  const SearchData = (): SearchData[] => {
    const searchData: SearchData[] = [];
    countries?.countries.map(country => {
      searchData.push({
        label: country.countryName,
        type: SearchType.Country,
        id: country.countryId,
      });
    });
    routes?.routes.map(route => {
      searchData.push({
        label: route.routeName,
        type: SearchType.Route,
        id: route.routeId,
      });
    });
    boats?.boats.map(boat => {
      searchData.push({
        label: boat.name,
        type: SearchType.Boat,
        id: boat.boatId,
      });
    });
    return searchData;
  };

  const rowsOfMonth = groupDataIntoRows(MonthData, 3);

  const handleSearchClick = () => {
    const queryParams = buildTokenizedQueryString({
      destinationId,
      monthId,
      diver,
      currentYear,
      searchType,
      destination,
      month,
    });

    navigate(`/search?${queryParams}`);
    location.reload();
  };

  return (
    <div
      className={`fixed top-0 z-50 h-full h-screen w-screen overflow-hidden bg-neutral-20 flex flex-col justify-between ${
        showSearchOverlay ? 'visible' : 'invisible'
      }`}
    >
      <div className="px-6 pt-4">
        <button type="button" onClick={() => setShowSearchOverlay(false)}>
          <IconX size={20} />
        </button>
        <>
          {expandWhere ? (
            <div className="flex flex-grow justify-between border border-neutral-100 rounded-3xl p-6 mt-4 bg-white">
              <div className="w-full flex flex-col justify-center">
                <div className="font-semibold ">Where</div>
                <div className="mt-4">
                  <Autocomplete
                    options={SearchData()}
                    onChange={(event: unknown, newValue: SearchData | null) =>
                      handleDestinationSearchChange(
                        event,
                        newValue?.label || '',
                        newValue?.type || 0,
                        newValue?.id || 0,
                      )
                    }
                    renderOption={(props, option) => (
                      <li {...props}>
                        <div className="rounded-2xl border border-neutral-100 p-3 mr-4">
                          {getSearchIcon(option.type)}
                        </div>
                        <div>
                          <div className="text-sm">{option.label}</div>
                          <div className="text-xs text-neutral-500">
                            {getSearchType(option.type)}
                          </div>
                        </div>
                      </li>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="Search"
                        fullWidth
                        slotProps={{
                          ...params.slotProps,
                          input: {
                            ...params.slotProps.input,
                            startAdornment: (
                              <IconSearch className="mr-2" size={20} />
                            ),
                          },
                        }}
                      />
                    )}
                  />
                </div>
                <div className="mt-4 font-semibold">Destination</div>
                <div className="mt-1">
                  {countries?.countries.map((country, index) => {
                    if (index % 2 !== 0) return null;
                    const nextCountry = countries?.countries[index + 1];

                    return (
                      <div
                        key={country.countryId}
                        className="grid grid-cols-2 gap-3"
                      >
                        <MultiStageSearchOption
                          option={country.countryName}
                          icon={<IconMapPin size={20} />}
                          action={() =>
                            handleDestinationBoxClick(
                              country.countryName,
                              SearchType.Country,
                              country.countryId,
                            )
                          }
                        />

                        {nextCountry && (
                          <MultiStageSearchOption
                            option={nextCountry.countryName}
                            icon={<IconMapPin size={20} />}
                            action={() =>
                              handleDestinationBoxClick(
                                nextCountry.countryName,
                                SearchType.Country,
                                nextCountry.countryId,
                              )
                            }
                          />
                        )}
                      </div>
                    );
                  })}
                  <div className="mt-4 flex justify-center ">
                    <button
                      className="text-sm font-semibold text-brand-santtai-primary"
                      onClick={() =>
                        handleDestinationBoxClick(
                          'Anywhere',
                          SearchType.AnyDestination,
                          0,
                        )
                      }
                    >
                      I'm flexible, search for anywhere
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="flex flex-grow justify-between border border-neutral-100 rounded-3xl p-6 mt-4 bg-white"
              onClick={() => {
                setExpandWhere(true);
                setExpandWhen(false);
              }}
            >
              <div className="text-neutral-500">Where</div>
              <div className="font-semibold text-neutral-800">
                {destination}
              </div>
            </div>
          )}
        </>
        <>
          {expandWhen ? (
            <div className="flex flex-grow justify-between border border-neutral-100 rounded-3xl p-6 mt-2 bg-white">
              <div className="w-full flex flex-col justify-center">
                <div className="font-semibold ">When</div>
                <div className="mt-4">
                  <div className="font-bold flex justify-between text-center">
                    {currentYear > new Date().getFullYear() ? (
                      <button
                        type="button"
                        onClick={() => setCurrentYear(currentYear - 1)}
                      >
                        <IconArrowLeft />
                      </button>
                    ) : (
                      <div className="w-6" />
                    )}
                    <div>{currentYear}</div>
                    <button
                      type="button"
                      onClick={() => setCurrentYear(currentYear + 1)}
                    >
                      <IconArrowRight />
                    </button>
                  </div>
                  <div>
                    {rowsOfMonth.map((row, index) => (
                      <div key={index} className="flex justify-between gap-3">
                        {row.map((moth: string) => (
                          <MultiStageSearchMonthOption
                            key={moth}
                            option={moth}
                            icon={<IconCalendarEvent />}
                            action={() =>
                              handleMonthBoxClick(
                                moth,
                                MonthData.indexOf(moth) + 1,
                              )
                            }
                            currentYear={currentYear}
                          />
                        ))}
                      </div>
                    ))}
                    <div className="mt-4 flex justify-center ">
                      <button
                        className="text-sm font-semibold text-brand-santtai-primary"
                        onClick={() => handleMonthBoxClick('Anytime', 0)}
                      >
                        I'm flexible, search for anytime
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="flex flex-grow justify-between border border-neutral-100 rounded-3xl p-6 mt-2 bg-white"
              onClick={() => {
                setExpandWhen(true);
                setExpandWhere(false);
              }}
            >
              <div className="text-neutral-500">When</div>
              <div className="font-semi font-bold text-neutral-800">
                {month === 'Anytime' ? month : month + ' ' + currentYear}
              </div>
            </div>
          )}
        </>
        <div className="flex flex-grow items-center justify-between border border-neutral-100 rounded-3xl p-6 mt-2 bg-white">
          <div className="text-neutral-500">Divers</div>
          <div className="font-semibold text-neutral-800">
            <button
              className={`p-2 border border-solid ${
                diver > 1
                  ? 'border-neutral-100 text-black'
                  : 'border-neutral-50 text-neutral-50'
              } rounded-full`}
              onClick={() => setDiver(diver - 1)}
              disabled={diver <= 1}
            >
              <IconMinus size={16} />
            </button>
            <span className="mx-2">{diver}</span>
            <button
              onClick={() => setDiver(diver + 1)}
              className="p-2 border border-solid border-neutral-100 rounded-full"
            >
              <IconPlus size={16} />
            </button>
          </div>
        </div>
      </div>
      <div className="flex justify-between fixed bottom-0 w-full h-16 px-6 py-3 bg-neutral-00 shadow-md">
        <button
          className="font-semibold underline text-neutral-800"
          onClick={() => handleClearAll()}
        >
          Clear all
        </button>
        <button
          className="px-4 py-3 h-[2.8rem] flex items-center bg-gradient-to-r from-brand-santtai-primary to-brand-santtai-primary-darker text-neutral-00 rounded-lg"
          onClick={() => handleSearchClick()}
        >
          <IconSearch className="mr-2" size={20} />
          Search
        </button>
      </div>
    </div>
  );
};
export default SearchOverlay;
